import { MinzeElement, EventListeners, Reactive, Attrs } from 'minze'
import MainModule from '../../modules/main'
import { Locale } from '../../modules/locale'
import { Font } from '../../constants'
import { Callback } from '../../modules/callback'
import { unescapeHtml } from '../../modules/utils'
import axios from 'redaxios'
import { config } from '../../constants/config'

Locale.getInstance().registerKeySet('fr', 'lvc-report-problem-popup', {
  title: 'Signaler un problème',
  subtitle: 'Que souhaitez-vous signaler à notre équipe?',
  pageLabel: 'Page',
  messageLabel: 'Message',
  messagePlaceholder: 'Décrivez le problème que vous avez rencontré',
  addScreenshotLabel: "Ajouter une capture d'écran",
  uploadImageLabel: 'Téléverser une image',
  deleteUploadedImageLabel: 'Supprimer',
  sendLabel: 'Envoyer',
  errors: {
    message:
      'Veuillez ajouter une description dans le champ “Message” avant de soumettre le rapport.',
    image:
      ' Erreur. Veuillez téléverser une image de moins de 5mb et au format .jpg, .jpeg ou .png.',
    general: 'Une erreur est survenue. Veuillez réessayer plus tard.',
  },
})

Locale.getInstance().registerKeySet('en', 'lvc-report-problem-popup', {
  title: 'Report a problem',
  subtitle: 'What do you wish to report to our team?',
  pageLabel: 'Page',
  messageLabel: 'Message',
  messagePlaceholder: 'Describe the problem you encountered',
  addScreenshotLabel: 'Add a screenshot',
  uploadImageLabel: 'Upload image',
  deleteUploadedImageLabel: 'Delete',
  sendLabel: 'Submit',
  errors: {
    message: 'Please provide a description in the “Message” field before submitting the report.',
    image:
      'Error. Please make sure the image does not exceed 5mb and is in .jpg, .jpeg or .png format.',
    general: 'An error occurred. Please try again later.',
  },
})

export interface LvcReportProblemPopup {
  onCancel?: string
  onContinue?: string
  filePath?: string
}

export class LvcReportProblemPopup extends MinzeElement {
  reactive: Reactive = [
    ['hasImage', false],
    ['shownImageName', ''],
    ['showRequestSent', false],
  ]

  selectedImage: File | null = null

  onReady(): void | Promise<void> {
    this.subject = MainModule.getInstance().getRouting().getCurrentPage()

    this.rerender()
  }

  onClickUpload(): void {
    // Trigger file input click
    const uploadInput = this.select<HTMLInputElement>('#upload-input')
    uploadInput?.click()
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement
    if (input.files && input.files[0]) {
      const file = input.files[0]
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png']
      const maxSize = 5 * 1024 * 1024 // 5MB

      if (allowedTypes.includes(file.type) && file.size <= maxSize) {
        try {
          this.filePath = await MainModule.getInstance().getAuth().uploadAnonymousImage(file)
          this.errors.image = false
        } catch (error) {
          this.errors.image = Locale.getKey('lvc-report-problem-popup.errors.general')
          this.rerender()
        }
        this.selectedImage = file
        this.hasImage = true
        // if the file name is too long, we show a truncated version
        if (file.name.length > 40) {
          this.shownImageName = file.name.substring(0, 40) + '...'
        } else {
          this.shownImageName = file.name
        }
      } else {
        let hasError = false
        this.errors.image = Locale.getKey('lvc-report-problem-popup.errors.image')
        hasError = true
        this.rerender()
      }
    }
  }

  onClickDelete(): void {
    this.selectedImage = null
    this.hasImage = false
    this.rerender()
  }

  errors: any = {
    message: false,
    general: false,
    image: false,
  }

  onSubmitForm = async () => {
    for (const key in this.errors) {
      this.errors[key] = false
    }
    const messageElement: any = this.select('#message')
    const message = unescapeHtml(messageElement?.value || '')

    if (!message) {
      this.errors.message = Locale.getKey('lvc-report-problem-popup.errors.message')
    }

    let hasErrors = false

    for (const key in this.errors) {
      if (this.errors[key]) {
        hasErrors = true
      }
    }

    if (hasErrors) {
      this.rerender()
      return
    }

    if (!hasErrors) {
      try {
        axios.post(
          `${config.backendApi}/user/auth/report_problem`,
          {
            subject: this.subject,
            message: message,
            image: this.filePath,
            page: window.location.toString(),
          },
          {
            headers: {
              Authorization: `Bearer ${MainModule.getInstance().getAuth().getAccessToken()}`,
            },
          }
        )

        // Show request sent immediately
        this.showRequestSent = true
      } catch (e) {
        this.errors.general = Locale.getKey('lvc-report-problem-popup.errors.general')
      }
    }
    this.rerender()
  }

  html = () => `

  ${
    this.showRequestSent
      ? `<lvc-report-problem-sent-popup></lvc-report-problem-sent-popup>`
      : `<lvc-simple-modal id="modal" modal-title="${Locale.getKey('lvc-report-problem-popup.title')}" title-size="22px" simple-close-button="true" text-transform="uppercase" background="white" max-width="512px">
      <div class="modal-content">
        <div class="subtitle-container">

          <lvc-text font-preset="medium">
          ${Locale.getKey('lvc-report-problem-popup.subtitle')}
          </lvc-text>

        </div>
        <div class="form-container">
          <lvc-error error="${this.errors.general}"></lvc-error>
          <div class="subject-container">
            <lvc-input disabled="true" value="${this.subject}" default-value="${this.subject}" label="${Locale.getKey('lvc-report-problem-popup.pageLabel')}" padding="0px"></lvc-input>

          </div>



          <div class="message-container">
              <lvc-input textarea="true" id="message" error="${this.errors.message}" resize="none" label="${Locale.getKey('lvc-report-problem-popup.messageLabel')}" placeholder="${Locale.getKey('lvc-report-problem-popup.messagePlaceholder')}">${this.message}</lvc-input>
          </div>

          <div class="screenshot-container">

            <lvc-text font-preset="smallExtraBold" class="already-logged-in-content" line-height="150%">
            ${Locale.getKey('lvc-report-problem-popup.addScreenshotLabel')}
            </lvc-text>
              <div class="screenshot-input-container">


            ${
              this.hasImage
                ? ` <lvc-text font-preset="small" text-decoration="underline">
            ${this.shownImageName}
            </lvc-text>
            <lvc-simple-button id="delete-image-button" name="delete-image-button" btn-preset="searchAll" text-decoration="underline">${Locale.getKey('lvc-report-problem-popup.deleteUploadedImageLabel')} </lvc-simple-button>
            `
                : `<lvc-button enable-hover-box-shadow="true" id="upload-button" name="upload-button" border-radius="999px" height="fit-content" width="fit-content" padding="13px 16px" >
            ${Locale.getKey('lvc-report-problem-popup.uploadImageLabel')}
            </lvc-button>
            <input id="upload-input" type="file" accept="image/*" style="display: none;">`
            }



            </div>
            ${
              this.errors.image
                ? `
            <lvc-box mt="12px" display="flex" width="100%" justifyContent="center">
              <lvc-error error="${this.errors.image}"></lvc-error>`
                : ''
            }
            </lvc-box>
          </div>

          <div class="bottom-buttons-container">

            <lvc-button enable-hover-box-shadow="true" id="submit-button" name="submit-button"border-radius="999px" height="fit-content" width="fit-content" padding="14px 24px" >
            ${Locale.getKey('lvc-report-problem-popup.sendLabel')}
            </lvc-button>

          </div>
        </div>
      </div>
    </lvc-simple-modal>`
  }

    `

  css = () => `
    :host {
      display: inline;
    }

    .modal-content{
      display: flex;
      flex-direction: column;
      justify-content: center;
    }

    .screenshot-input-container{
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
    }

    .form-container {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    .screenshot-container{
      display: flex;
      flex-direction: column;
      gap: 16px;

    }

    .bottom-buttons-container{
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
    }

    .subject-container{
      display: flex;
      justify-content: flex-end;
      margin-top: 15px;
      margin-bottom: 15px;
    }

    .subtitle-container {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      margin-top: 12px;
      margin-left: 0;
    }

  `

  eventListeners: EventListeners = [
    ['[name="upload-button"]', 'click', this.onClickUpload.bind(this)],
    ['[name="submit-button"]', 'click', this.onSubmitForm.bind(this)],
    ['#upload-input', 'change', this.onFileSelected.bind(this)],
    ['[name="delete-image-button"]', 'click', this.onClickDelete.bind(this)],
  ]
}
